import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { useGetMultidietCaterings } from "@/features/multidiet/api/hooks/use-get-multidiet-caterings";
import type { MultidietCateringsTierType } from "@/features/multidiet/types/multidiet-caterings.type";
import { MultidietCateringsModal } from "@/features/multidiet-picker/components/multidiet-caterings-modal";
import type { MultidietSetupVariantType } from "@/features/multidiet-picker/types/multidiet-setup.type";
import { cn } from "@/lib/utils";

export const MultidietVariantsItem = ({
  size = "small",
  isBestOffer = false,
  layout = "page",
  variant,
}: {
  size?: "small" | "large";
  isBestOffer?: boolean;
  layout?: "page" | "popup";
  variant: MultidietSetupVariantType;
}) => {
  const t = useTranslations();
  const params = useSearchParams();
  const type = params.get("type");

  const tier = variant.type.toLowerCase() as MultidietCateringsTierType;
  const { data: caterings } = useGetMultidietCaterings({
    tier,
  });

  return (
    <div
      className={cn(
        "relative rounded-xl border-2 px-6 py-8",
        layout === "page" ? "bg-white" : "bg-brand-grey1",
        isBestOffer
          ? "basis-[38%] border-brand-red"
          : "basis-[31%] border-transparent",
        type && type === variant.type && "border-brand-green-300",
        type && type !== variant.type && "border-transparent",
      )}
    >
      {isBestOffer && (
        <p className="absolute -top-3.5 right-4 rounded-4xl bg-brand-red px-3 py-1.5 font-medium text-[11px] text-white uppercase tracking-wider">
          {t("multidiet-picker.best-offer")}
        </p>
      )}
      <div>
        <header className={cn(size === "large" ? "mb-12" : "mb-8")}>
          <h3
            className={cn(
              size === "large" ? "text-2xl" : "text-xl",
              "font-medium",
            )}
          >
            {t(`multidiet-picker.variants-names.${variant.type}`)}
          </h3>
          <div
            className={cn(
              size === "large" ? "text-lg" : "text-sm",
              "mt-1 leading-5",
            )}
          >
            <p>
              {t.rich(`multidiet-picker.bonus.${variant.type}`, {
                caterings: (chunks) => (
                  <MultidietCateringsModal
                    data={caterings ?? []}
                    layout={layout}
                    trigger={
                      <button
                        className="cursor-pointer font-bold underline underline-offset-3 hover:text-brand-black/70"
                        type="button"
                      >
                        {chunks}
                      </button>
                    }
                    variant={variant}
                  />
                ),
                value: caterings ? caterings.length : 0,
              })}
            </p>
          </div>
        </header>
        <div className={cn(size === "large" ? "mb-6" : "mb-4")}>
          <p
            className={cn(
              "mb-1 text-brand-green1-300",
              size === "large" ? "text-sm" : "text-xs",
            )}
          >
            {t("multidiet-picker.average-price")}
          </p>
          <p
            className={cn(
              size === "large" ? "text-xl leading-7" : "text-lg leading-6",
            )}
          >
            {t.rich("multidiet-picker.price-per-day", {
              price: (chunks) => (
                <span
                  className={cn(
                    "font-medium",
                    size === "large" ? "text-2xl" : "text-xl",
                  )}
                >
                  {chunks}
                </span>
              ),
              value: variant.price,
            })}
          </p>
        </div>
        <Button
          className={cn("w-full", size === "large" ? "h-12" : "h-11")}
          data-dietvariant={JSON.stringify(variant)}
          size="lg"
          type="submit"
          variant="secondary"
        >
          {t("multidiet-picker.choose")}
        </Button>
      </div>
    </div>
  );
};
